import { useEffect, useState } from "react";
import { PaperPlaneTilt, Shield, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const PRESET = {
  subject: "The Pack Dispatch — this month at the Sanctuary",
  body: "Warriors,\n\nHere's the latest from Heaven Ranch — the pits we saved, the ground we broke, and the drops that funded it.\n\n• Rescues this month: \n• Ranch progress: \n• New drop: \n\nEvery hoodie you wear puts a pit off the street. Thank you for standing in the gap.\n\n— Mr. Wigglez",
};

export default function NewsletterAdmin() {
  const [stats, setStats] = useState({ total: 0, warriors: 0, recent: [] });
  const [audience, setAudience] = useState("warriors-sanctuary");
  const [subject, setSubject] = useState(PRESET.subject);
  const [body, setBody] = useState(PRESET.body);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/newsletter/stats").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const count = audience === "all" ? stats.total : stats.warriors;

  const send = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Subject and message required");
    if (!window.confirm(`Send "${subject}" to ${count} subscriber${count === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", { subject, message_html: body, audience });
      toast.success(`Newsletter sent to ${data.sent}/${data.total} 🐾`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-newsletter">
      <div className="flex items-center gap-3 mb-2">
        <Shield weight="fill" size={20} className="text-secondary" />
        <h2 className="font-display text-2xl">Warrior's Sanctuary · Newsletter</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Compose "The Pack Dispatch" and blast it to your supporters via Resend. Branded template, replies land in your inbox.</p>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="rounded-xl border border-secondary/25 bg-card py-4 text-center" data-testid="nl-warriors">
          <p className="text-3xl font-modern font-semibold text-secondary">{stats.warriors}</p>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mt-1">Warrior's Sanctuary</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-card py-4 text-center" data-testid="nl-total">
          <p className="text-3xl font-modern font-semibold text-white">{stats.total}</p>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mt-1">All subscribers</p>
        </div>
      </div>

      <label className="overline text-muted-foreground">Audience</label>
      <select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full mt-1.5 mb-4 bg-card border border-white/15 rounded-[10px] px-4 py-3 text-sm outline-none" data-testid="nl-audience">
        <option value="warriors-sanctuary">{`Warrior's Sanctuary subscribers (${stats.warriors})`}</option>
        <option value="all">{`All subscribers (${stats.total})`}</option>
      </select>

      <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="w-full bg-transparent border border-white/15 rounded-[10px] px-4 py-3 text-sm outline-none focus:border-secondary mb-3" data-testid="nl-subject" />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="w-full bg-transparent border border-white/15 rounded-[10px] px-4 py-3 text-sm outline-none focus:border-secondary resize-none" data-testid="nl-body" />

      <button onClick={send} disabled={busy || count === 0} className="mt-4 inline-flex items-center gap-2 bg-secondary text-black px-6 py-3 rounded-[10px] font-modern font-medium disabled:opacity-40" data-testid="nl-send">
        <PaperPlaneTilt weight="fill" size={16} /> {busy ? "Sending…" : `Send to ${count}`}
      </button>

      {(stats.recent || []).length > 0 && (
        <div className="mt-8">
          <p className="overline text-muted-foreground mb-3 inline-flex items-center gap-2"><Users size={13} /> Recent dispatches</p>
          <div className="space-y-2">
            {stats.recent.map((r, i) => (
              <div key={i} className="rounded-lg border border-white/10 p-3 text-sm flex justify-between">
                <span className="text-white truncate">{r.subject}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap ml-3">{r.sent} sent · {new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
